import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_TEXTAREA_CLASS,
} from '../../settings/utils/settingsSectionStyles';

type Project = {
  id: string;
  name: string;
  description: string;
  instructions: string;
  lead_id: string | null;
  lead_label: string | null;
};

type LeadOption = { id: string; label: string };

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({})) as { error?: string };
  return body.error || fallback;
}

export function ChatProjectsView({
  activeProjectId,
  onOpenProject,
}: {
  activeProjectId: string | null;
  onOpenProject: (projectId: string | null) => void;
}) {
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [editing, setEditing] = React.useState<Project | "new" | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ai/chat-projects', { credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Projekte konnten nicht geladen werden.'));
      const body = await res.json() as { projects?: Project[] };
      setProjects(body.projects ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Projekte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-app-text">Projekte</h2>
        <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => setEditing('new')}>Neu</button>
      </div>
      <p className="mb-4 text-sm text-app-text-muted">Ein KI-Projekt bündelt Chats und eine Anweisung. Die Verknüpfung mit einem bestehenden Projekt öffnet dessen Daten nicht für andere.</p>
      {loading ? <InlineLoadingState label="Projekte werden geladen …" /> : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {editing ? (
        <ProjectEditor
          project={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      ) : (
        <ul className="divide-y divide-app-border rounded-md border border-app-border bg-app-surface">
          {projects.map((project) => (
            <li key={project.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <button type="button" className={`min-w-0 flex-1 text-left ${project.id === activeProjectId ? 'font-medium' : ''}`} onClick={() => onOpenProject(project.id === activeProjectId ? null : project.id)}>
                <span className="block truncate text-sm text-app-text">{project.name}</span>
                <span className="block truncate text-xs text-app-text-muted">{project.lead_label || 'Chat in diesem Projekt starten'}</span>
              </button>
              <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={() => setEditing(project)}>Bearbeiten</button>
            </li>
          ))}
          {!loading && projects.length === 0 ? <li className="px-3 py-4 text-sm text-app-text-muted">Noch keine KI-Projekte.</li> : null}
        </ul>
      )}
    </div>
  );
}

function ProjectEditor({
  project,
  onCancel,
  onSaved,
}: {
  project: Project | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = React.useState(project?.name ?? '');
  const [instructions, setInstructions] = React.useState(project?.instructions ?? '');
  const [leadQuery, setLeadQuery] = React.useState('');
  const [leadOptions, setLeadOptions] = React.useState<LeadOption[]>([]);
  const [leadId, setLeadId] = React.useState<string | null>(project?.lead_id ?? null);
  const [leadLabel, setLeadLabel] = React.useState(project?.lead_label ?? '');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const needle = leadQuery.trim();
    if (!needle) {
      setLeadOptions([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void fetch(`/api/ai/chat-projects/lead-options?q=${encodeURIComponent(needle)}`, { credentials: 'include' })
        .then((res) => res.ok ? res.json() as Promise<{ leads?: LeadOption[] }> : { leads: [] })
        .then((body) => setLeadOptions(body.leads ?? []))
        .catch(() => setLeadOptions([]));
    }, 200);
    return () => window.clearTimeout(handle);
  }, [leadQuery]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(project ? `/api/ai/chat-projects/${project.id}` : '/api/ai/chat-projects', {
        method: project ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, instructions, leadId }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Projekt konnte nicht gespeichert werden.'));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Projekt konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!project) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/ai/chat-projects/${project.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Projekt konnte nicht gelöscht werden.'));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Projekt konnte nicht gelöscht werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-app-border bg-app-surface p-4">
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Name</span>
        <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Anweisung</span>
        <textarea className={`${SETTINGS_TEXTAREA_CLASS} mt-1.5 w-full`} rows={5} value={instructions} onChange={(event) => setInstructions(event.target.value)} />
      </label>
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Bestehendes Projekt</span>
        <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={leadQuery} placeholder={leadLabel || 'Name oder Nummer'} onChange={(event) => setLeadQuery(event.target.value)} />
      </label>
      {leadOptions.length > 0 ? (
        <ul className="rounded-md border border-app-border">
          {leadOptions.map((lead) => (
            <li key={lead.id}>
              <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-app-hover" onClick={() => { setLeadId(lead.id); setLeadLabel(lead.label); setLeadOptions([]); }}>
                {lead.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {leadId ? (
        <button type="button" className="text-sm text-app-text-muted underline" onClick={() => { setLeadId(null); setLeadLabel(''); }}>Verknüpfung entfernen{leadLabel ? `: ${leadLabel}` : ''}</button>
      ) : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <div className="flex justify-end gap-2">
        {project ? <button type="button" className={`${SETTINGS_BTN_DANGER_OUTLINE} mr-auto`} onClick={() => void remove()}>Löschen</button> : null}
        <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={onCancel}>Abbrechen</button>
        <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving} onClick={() => void save()}>Speichern</button>
      </div>
    </div>
  );
}
